import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { fmtMontant, MOIS } from "@/lib/format";
import { PageHeader, StatusBadge } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { Users, FileText, FileCheck2, Wallet, TrendingDown, Loader2 } from "lucide-react";

const ALL = "__all__";

function Metric({ icon: Icon, label, value, accent }) {
  return (
    <Card className="border-slate-200 p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
        <Icon className={`h-5 w-5 ${accent || "text-slate-400"}`} strokeWidth={1.75} />
      </div>
      <p className="mt-3 font-mono text-2xl font-bold tracking-tight text-slate-900">{value}</p>
    </Card>
  );
}

function YearComparison({ annees }) {
  const [ya, setYa] = useState("");
  const [yb, setYb] = useState("");
  const [rows, setRows] = useState(null);

  useEffect(() => {
    if (annees.length && !ya) setYa(annees[0]);
    if (annees.length > 1 && !yb) setYb(annees[1]);
    else if (annees.length === 1 && !yb) setYb(annees[0]);
  }, [annees, ya, yb]);

  useEffect(() => {
    if (!ya || !yb) return;
    Promise.all([
      api.get("/dashboard", { params: { annee: ya } }),
      api.get("/dashboard", { params: { annee: yb } }),
    ]).then(([a, b]) => {
      setRows([
        { metric: "Débit", [ya]: a.data.total_debit, [yb]: b.data.total_debit },
        { metric: "Apurement", [ya]: a.data.total_apurement, [yb]: b.data.total_apurement },
        { metric: "Solde", [ya]: a.data.solde, [yb]: b.data.solde },
      ]);
    });
  }, [ya, yb]);

  if (!annees.length) return null;

  return (
    <Card className="mt-6 border-slate-200 p-5 shadow-sm" data-testid="year-comparison">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="font-heading text-base font-semibold text-slate-800">Comparaison des recouvrements</h3>
        <div className="flex items-center gap-2">
          <Select value={ya} onValueChange={setYa}>
            <SelectTrigger className="w-32 bg-white" data-testid="compare-year-a"><SelectValue placeholder="Année A" /></SelectTrigger>
            <SelectContent>{annees.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
          </Select>
          <span className="text-slate-400">vs</span>
          <Select value={yb} onValueChange={setYb}>
            <SelectTrigger className="w-32 bg-white" data-testid="compare-year-b"><SelectValue placeholder="Année B" /></SelectTrigger>
            <SelectContent>{annees.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      {rows && (
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={rows} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="metric" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={{ stroke: "#e2e8f0" }} />
            <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false}
              tickFormatter={(v) => (Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} />
            <Tooltip formatter={(v) => fmtMontant(v)} contentStyle={{ borderRadius: 6, borderColor: "#e2e8f0", fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey={ya} fill="#0f172a" radius={[3, 3, 0, 0]} />
            <Bar dataKey={yb} fill="#d97706" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [annee, setAnnee] = useState(ALL);
  const [mois, setMois] = useState(ALL);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (annee !== ALL) params.annee = annee;
    if (mois !== ALL) params.mois = mois;
    const { data } = await api.get("/dashboard", { params });
    setData(data);
    setLoading(false);
  }, [annee, mois]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading || !data) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const chartData = data.mensuel.map((m) => ({
    mois: m.mois.slice(0, 3),
    Débit: m.debit,
    Apurement: m.apurement,
  }));

  return (
    <div data-testid="dashboard">
      <PageHeader title="Accueil" subtitle="Situation générale des paiements par acte générateur">
        <Select value={mois} onValueChange={setMois}>
          <SelectTrigger className="w-40 bg-white" data-testid="dashboard-month">
            <SelectValue placeholder="Mois" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Tous les mois</SelectItem>
            {MOIS.map((m) => (
              <SelectItem key={m} value={m}>{m}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={annee} onValueChange={setAnnee}>
          <SelectTrigger className="w-40 bg-white" data-testid="dashboard-year">
            <SelectValue placeholder="Année" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Toutes les années</SelectItem>
            {data.annees_disponibles.map((a) => (
              <SelectItem key={a} value={a}>{a}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </PageHeader>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Metric icon={Users} label="Effectif Général" value={data.effectif_general} accent="text-slate-700" />
        <Metric icon={FileText} label="Total Débit" value={fmtMontant(data.total_debit)} accent="text-amber-600" />
        <Metric icon={FileCheck2} label="Total Apurement" value={fmtMontant(data.total_apurement)} accent="text-emerald-600" />
        <Metric icon={TrendingDown} label="Solde Restant" value={fmtMontant(data.solde)} accent="text-red-600" />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-3">
        <Card className="border-slate-200 p-5 shadow-sm xl:col-span-2">
          <div className="mb-4 flex items-center gap-2">
            <Wallet className="h-4 w-4 text-slate-500" />
            <h3 className="font-heading text-base font-semibold text-slate-800">Situation mensuelle des paiements</h3>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="mois" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={{ stroke: "#e2e8f0" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false}
                tickFormatter={(v) => (v >= 1000 ? `${v / 1000}k` : v)} />
              <Tooltip formatter={(v) => fmtMontant(v)} contentStyle={{ borderRadius: 6, borderColor: "#e2e8f0", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Débit" fill="#d97706" radius={[3, 3, 0, 0]} />
              <Bar dataKey="Apurement" fill="#059669" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="border-slate-200 p-5 shadow-sm">
          <h3 className="mb-4 font-heading text-base font-semibold text-slate-800">Répartition</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Note de perception</span>
                <span className="font-mono font-semibold text-slate-800">{data.nb_notes_debit}</span>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Notes d'apurement</span>
                <span className="font-mono font-semibold text-slate-800">{data.nb_notes_apurement}</span>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Actes générateurs</span>
                <span className="font-mono font-semibold text-slate-800">{data.nb_actes}</span>
              </div>
            </div>
            <div className="border-t border-slate-100 pt-4">
              <p className="text-xs uppercase tracking-wider text-slate-400">Taux de recouvrement</p>
              <p className="mt-1 font-mono text-2xl font-bold text-slate-900">
                {data.total_debit > 0 ? ((data.total_apurement / data.total_debit) * 100).toFixed(1) : "0.0"}%
              </p>
            </div>
          </div>
        </Card>
      </div>

      <Card className="mt-6 border-slate-200 shadow-sm">
        <div className="border-b border-slate-100 px-5 py-4">
          <h3 className="font-heading text-base font-semibold text-slate-800">Situation par acte générateur</h3>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Acte Générateur</TableHead>
              <TableHead className="text-right">Nb Notes</TableHead>
              <TableHead className="text-right">Total Débit</TableHead>
              <TableHead className="text-right">Total Apurement</TableHead>
              <TableHead className="text-right">Solde</TableHead>
              <TableHead className="text-center">Statut</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.par_acte.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-10 text-center text-slate-400">
                  Aucune donnée disponible
                </TableCell>
              </TableRow>
            ) : (
              data.par_acte.map((r) => (
                <TableRow key={r.acte} className="hover:bg-slate-50" data-testid="dashboard-acte-row">
                  <TableCell className="font-medium text-slate-800">{r.acte}</TableCell>
                  <TableCell className="text-right font-mono text-slate-600">{r.nb_notes}</TableCell>
                  <TableCell className="text-right font-mono text-amber-700">{fmtMontant(r.total_debit)}</TableCell>
                  <TableCell className="text-right font-mono text-emerald-700">{fmtMontant(r.total_apurement)}</TableCell>
                  <TableCell className="text-right font-mono font-semibold text-slate-900">{fmtMontant(r.solde)}</TableCell>
                  <TableCell className="text-center"><StatusBadge solde={r.solde} /></TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <YearComparison annees={data.annees_disponibles} />
    </div>
  );
}
